import type { V2CardDetailResponse } from '../types/v2';

/**
 * Loads a card's details for the card screen, one card at a time. Only the
 * latest load may land: opening another card, or leaving for Top-up, while
 * the last card's details are still on their way must not put the old card's
 * figures under the new one.
 *
 * `cancel` stops the current load AND its spinner. The first version of this
 * only made the load stale: its `finally` then no longer cleared the spinner,
 * and a card opened, left for Top-up mid-load and gone back to stayed on the
 * loading state until it was opened again.
 */
export interface CardDetailLoader {
  /** Starts loading `cardId`'s details; an earlier load still out is dropped. */
  load: (cardId: string) => Promise<void>;
  /** Drops the load still out, if any, and clears the spinner. */
  cancel: () => void;
}

export const createCardDetailLoader = (deps: {
  getCard: (cardId: string) => Promise<V2CardDetailResponse>;
  setDetail: (detail: V2CardDetailResponse | null) => void;
  setLoading: (loading: boolean) => void;
}): CardDetailLoader => {
  let current = 0;

  const load = async (cardId: string): Promise<void> => {
    const ticket = ++current;
    const isLatest = () => ticket === current;
    deps.setDetail(null);
    deps.setLoading(true);
    try {
      if (!cardId) {
        return;
      }
      // The card's figures come from Payoma through our backend, which
      // answers 502 when the upstream is briefly unavailable. One retry
      // covers that; the screen falls back to the stored balance after.
      let detail: V2CardDetailResponse;
      try {
        detail = await deps.getCard(cardId);
      } catch (first) {
        console.warn('[CardDetail] Card detail failed, retrying once:', first);
        if (!isLatest()) {
          return;
        }
        detail = await deps.getCard(cardId);
      }
      if (isLatest()) {
        deps.setDetail(detail);
      }
    } catch (err) {
      console.error('[CardDetail] Failed to load card detail:', err);
    } finally {
      if (isLatest()) {
        deps.setLoading(false);
      }
    }
  };

  const cancel = () => {
    current += 1;
    deps.setLoading(false);
  };

  return { load, cancel };
};
